/** Renderização de Talemark (docs/spec/talemark.md) em React. */
import type { CSSProperties, ReactNode } from 'react';
import { useFileUrl } from '../files.js';
import { useT } from '../i18n/index.js';
import { DEFAULT_TALEMARK_OPTIONS, deskFileRefId, parseTalemark, type TMBlock, type TMClassicStyle, type TMInline, type TalemarkOptions } from '../shared.js';
import { talemarkPrefs } from '../settings.js';
import { useApp } from '../store.js';
import { serverUrl } from '../serverUrl.js';
import { ViewableImage } from '../ui/ImageViewer.js';

/**
 * `[§I rpgdesk-file:<id>]`: imagem do Desk Cloud no meio do texto (a de um plugin, como o ataque de
 * uma ficha). O link assinado expira, então é pedido na hora, com a permissão de quem vê.
 */
function DeskFileImage({ fileId }: { fileId: string }) {
  const t = useT();
  const url = useFileUrl(fileId);
  if (url === 'error') return <span className="tm-imageMissing">{t('chat.image.missing')}</span>;
  if (!url) return <span className="tm-image tm-image--loading" />;
  return <ViewableImage className="tm-imageThumb" imgClassName="tm-image" src={url} />;
}

const SPAN_WRAP: Record<string, [string, string]> = {
  action: ['●', '●'],
  quote: ['“', '”'],
  emdash: ['— ', ' —'],
  ooc: ['【', '】'],
};

function classicStyle(s: TMClassicStyle | undefined): CSSProperties | undefined {
  if (!s) return undefined;
  const deco = [s.underline ? 'underline' : '', s.strike ? 'line-through' : ''].filter(Boolean).join(' ');
  return {
    ...(s.bold ? { fontWeight: 700 } : {}),
    ...(s.italic ? { fontStyle: 'italic' } : {}),
    ...(deco ? { textDecoration: deco } : {}),
    ...(s.color ? { color: s.color } : {}),
    ...(s.background ? { background: s.background } : {}),
  };
}

function renderInline(items: TMInline[], keyPrefix: string): ReactNode[] {
  return items.map((it, i) => {
    const key = `${keyPrefix}${i}`;
    switch (it.t) {
      case 'text':
        return it.style ? (
          <span key={key} style={classicStyle(it.style)}>
            {it.text}
          </span>
        ) : (
          it.text
        );
      case 'br':
        return <br key={key} />;
      case 'initialCap':
        return (
          <span key={key} className="tm-initialCap">
            {it.char}
          </span>
        );
      case 'url':
        return (
          <a key={key} href={serverUrl(it.href)} target="_blank" rel="noreferrer noopener">
            {it.href}
          </a>
        );
      case 'mention':
        return (
          <span key={key} className="tm-mention">
            @{it.name}
          </span>
        );
      case 'image': {
        const fileId = deskFileRefId(it.src);
        if (fileId) return <DeskFileImage key={key} fileId={fileId} />;
        // clicar abre a imagem grande, como a enviada ao chat (BRAZ-21)
        return <ViewableImage key={key} className="tm-imageThumb" imgClassName="tm-image" src={serverUrl(it.src)} />;
      }
      case 'span': {
        const wrap = SPAN_WRAP[it.kind];
        const children = renderInline(it.children, `${key}.`);
        if (it.kind === 'em') return <em key={key}>{children}</em>;
        if (it.kind === 'strong') return <strong key={key}>{children}</strong>;
        return (
          <span key={key} className={`tm-${it.kind}`}>
            {wrap?.[0]}
            {children}
            {wrap?.[1]}
          </span>
        );
      }
    }
  });
}

/** Sem `options`, vale o que a pessoa escolheu em Configurações → Texto. */
export function TalemarkView({ text, options, inline = false }: { text: string; options?: TalemarkOptions; inline?: boolean }) {
  const settings = useApp((s) => s.settings);
  const blocks: TMBlock[] = parseTalemark(text, options ?? { ...DEFAULT_TALEMARK_OPTIONS, ...talemarkPrefs(settings) });
  if (inline && blocks.length === 1 && blocks[0]!.t === 'p') return <>{renderInline(blocks[0]!.children, 'i')}</>;
  return (
    <>
      {blocks.map((b, i) => {
        if (b.t === 'hr') return <hr key={i} className="tm-hr" />;
        if (b.t === 'h') {
          const H = `h${Math.min(6, b.level + 2)}` as 'h3';
          return (
            <H key={i} className="tm-heading">
              {renderInline(b.children, `${i}.`)}
            </H>
          );
        }
        return (
          <div key={i} className="tm-p">
            {renderInline(b.children, `${i}.`)}
          </div>
        );
      })}
    </>
  );
}
